import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PackageCheck, Bell, Clock, TrendingUp, TrendingDown, DollarSign,
  Star, MessageSquare, AlertTriangle, RefreshCw, ArrowRight, Loader2, Sparkles, Users, Zap,
} from 'lucide-react';
import { adminGetOverview, adminDailyBriefing, adminGetAttention } from '../../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 120, damping: 18 } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const Tile = ({ icon: Icon, label, value, sub, accent, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    disabled={!onClick}
    className={`text-left border rounded-xl p-5 bg-alp-card/50 border-alp-line transition-colors ${onClick ? 'hover:border-alp-gold/70 cursor-pointer' : 'cursor-default'}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{label}</span>
      <Icon size={16} className={accent || 'text-alp-gold'} />
    </div>
    <div className="mt-3 text-2xl md:text-3xl font-serif text-alp-cream">{value}</div>
    {sub && <div className="mt-1 text-[11px] text-alp-gray flex items-center gap-1">{sub}</div>}
  </button>
);

const STATUS_STYLE = {
  'Ordered': 'text-alp-cream/80', 'Payment Pending': 'text-amber-300', 'Payment Confirmed': 'text-emerald-300',
  'Packed': 'text-sky-300', 'Shipped': 'text-sky-300', 'Delivered': 'text-emerald-300', 'Cancelled': 'text-red-300',
};

export default function OverviewPanel({ goTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [attention, setAttention] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminGetOverview()); } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); adminDailyBriefing().then(setBrief).catch(() => {}); adminGetAttention().then(setAttention).catch(() => {}); }, [load]);

  if (loading && !data) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-alp-gold" size={26} /></div>;
  }
  if (!data) return null;

  const { queues, money: m, engagement, lowStock, recentOrders } = data;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-8" data-testid="overview-panel">
      {/* Warm greeting hero */}
      <motion.div variants={fadeUp} className="alp-beam rounded-3xl p-[1.5px]">
        <div className="rounded-[calc(1.5rem-1.5px)] bg-alp-black/95 backdrop-blur-2xl px-7 py-6 relative overflow-hidden">
          <div className="absolute -top-20 right-10 w-64 h-64 rounded-full bg-alp-gold/10 blur-3xl pointer-events-none" />
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-alp-gold mb-2 flex items-center gap-2"><Sparkles size={12} className="animate-pulse" /> {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-alp-cream heading-glow">{greeting()}, welcome back.</h2>
              <p className="text-alp-cream/60 text-sm mt-2" data-testid="overview-hero-summary">
                {queues.ordersToPack > 0
                  ? <>You have <span className="text-alp-gold">{queues.ordersToPack} order{queues.ordersToPack === 1 ? '' : 's'} ready to pack</span> and {money(m.moneyIn)} banked all-time. Let&apos;s make it a good one.</>
                  : <>Nothing waiting to be packed &mdash; {money(m.moneyIn)} banked all-time across {data.totalOrders} orders. Smooth sailing.</>}
              </p>
            </div>
            <button onClick={load} disabled={loading} data-testid="overview-refresh" className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase border border-alp-line px-4 py-2 rounded-full text-alp-cream/70 hover:border-alp-gold hover:text-alp-gold transition-colors disabled:opacity-50">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>
      </motion.div>

      {/* AI Daily Briefing */}
      {brief && brief.text && (
        <motion.div variants={fadeUp} data-testid="overview-daily-briefing">
          <div className="border border-alp-gold/30 rounded-xl bg-alp-card/40 px-6 py-5">
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-2 flex items-center gap-1.5"><Sparkles size={12} /> Today&apos;s briefing</p>
            <div className="text-alp-cream/85 text-sm leading-relaxed whitespace-pre-line">{brief.text}</div>
            {brief.actions && brief.actions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="briefing-actions">
                {brief.actions.map((a, i) => (
                  <button key={i} data-testid={`briefing-action-${i}`}
                    onClick={() => { try { localStorage.setItem('alp_admin_ai_pending', a.command); } catch (e) {} goTo('advisor', 'assistant'); }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.1em] uppercase border border-alp-gold/50 text-alp-gold hover:bg-alp-gold hover:text-alp-black transition-colors">
                    <Zap size={11} /> {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Action queues */}
      <motion.div variants={fadeUp}>
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Needs Action</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <Tile testid="overview-to-pack" icon={PackageCheck} label="Orders to pack" value={queues.ordersToPack}
            sub={<span>Payment confirmed <ArrowRight size={11} /></span>} onClick={() => goTo('sales', 'fulfillment')} />
          <Tile testid="overview-new-orders" icon={Bell} label="New orders" value={queues.newOrders} accent="text-red-400"
            sub={<span>Ordered / pending payment <ArrowRight size={11} /></span>} onClick={() => goTo('sales', 'orders')} />
          <Tile testid="overview-review-due" icon={Clock} label="Due a review nudge" value={queues.reviewDue} accent="text-emerald-300"
            sub={<span>Placed 10+ days ago <ArrowRight size={11} /></span>} onClick={() => goTo('sales', 'orders')} />
          {attention && <>
            <Tile testid="overview-awaiting-payment" icon={DollarSign} label="Awaiting payment" value={attention.awaitingPayment} accent="text-amber-300"
              sub={<span>Last 14 days <ArrowRight size={11} /></span>} onClick={() => goTo('sales', 'orders')} />
            <Tile testid="overview-abandoned" icon={AlertTriangle} label="Abandoned carts" value={attention.abandonedCarts} accent="text-amber-300"
              sub={<span>Send a 10% code <ArrowRight size={11} /></span>} onClick={() => goTo('sales', 'abandoned')} />
            <Tile testid="overview-payouts" icon={Users} label="Crew payout requests" value={attention.pendingPayouts} accent="text-emerald-300"
              sub={<span>Waiting on you <ArrowRight size={11} /></span>} onClick={() => goTo('crew', 'crew')} />
            <Tile testid="overview-low-stock" icon={TrendingDown} label="Low stock products" value={attention.lowStock} accent="text-red-400"
              sub={<span>3 units or fewer <ArrowRight size={11} /></span>} onClick={() => goTo('inventory', 'stock')} />
          </>}
        </div>
      </motion.div>

      {/* This month pulse */}
      {data.monthPulse && (() => {
        const p = data.monthPulse;
        const delta = p.lastMonth.revenue > 0 ? Math.round(((p.thisMonth.revenue - p.lastMonth.revenue) / p.lastMonth.revenue) * 100) : null;
        const pct = p.lastMonth.revenue > 0 ? Math.min(100, Math.round((p.thisMonth.revenue / p.lastMonth.revenue) * 100)) : (p.thisMonth.revenue > 0 ? 100 : 0);
        return (
          <motion.div variants={fadeUp} data-testid="overview-month-pulse">
            <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">This Month</p>
            <div className="border border-alp-gold/30 rounded-xl bg-alp-card/40 p-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="font-serif text-3xl text-alp-cream">{money(p.thisMonth.revenue)}</span>
                  <span className="text-alp-gray text-xs ml-2">banked · {p.thisMonth.orders} order{p.thisMonth.orders === 1 ? '' : 's'}</span>
                  {delta !== null && (
                    <span className={`ml-3 text-xs inline-flex items-center gap-1 ${delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{delta >= 0 ? '+' : ''}{delta}% vs last month
                    </span>
                  )}
                </div>
                {p.bestSeller && (
                  <span className="text-xs text-alp-gold border border-alp-gold/40 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5">
                    <Star size={12} /> Top seller: {p.bestSeller.name} ({p.bestSeller.units} units)
                  </span>
                )}
              </div>
              <div className="mt-4">
                <div className="h-2 rounded-full bg-alp-line/50 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-alp-gold/70 to-alp-gold rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-alp-gray text-[10px] mt-1.5">{pct >= 100 ? "You've beaten last month already — keep it rolling." : `${pct}% of last month's ${money(p.lastMonth.revenue)} — the month isn't over yet.`}</p>
              </div>
            </div>
          </motion.div>
        );
      })()}

      {/* Money */}
      <motion.div variants={fadeUp}>
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Money</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile testid="overview-money-in" icon={DollarSign} label="Money in (paid)" value={money(m.moneyIn)} accent="text-emerald-300"
            onClick={() => goTo('financial', 'pnl')} sub={<span>View P&amp;L <ArrowRight size={11} /></span>} />
          <Tile testid="overview-money-spent" icon={TrendingDown} label="Money spent" value={money(m.moneySpent)} accent="text-red-400"
            onClick={() => goTo('inventory', 'stock')} sub={<span>Stock + expenses <ArrowRight size={11} /></span>} />
          <Tile testid="overview-net-profit" icon={TrendingUp} label="Profit on sold items" value={money(m.netProfit)}
            accent={m.netProfit >= 0 ? 'text-emerald-300' : 'text-red-400'} onClick={() => goTo('financial', 'pnl')}
            sub={<span>Sales &minus; cost of units sold</span>} />
          <Tile testid="overview-cash-position" icon={DollarSign} label="Cash position" value={money(m.cashPosition ?? (m.moneyIn - m.moneySpent))}
            accent={(m.cashPosition ?? (m.moneyIn - m.moneySpent)) >= 0 ? 'text-emerald-300' : 'text-red-400'}
            onClick={() => goTo('financial', 'forecast')}
            sub={<span>Money in &minus; ALL money spent</span>} />
        </div>
      </motion.div>

      {/* Engagement + stock */}
      <motion.div variants={fadeUp}>
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Members, Reviews &amp; Stock</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Tile testid="overview-members" icon={Users} label="Registered members" value={(data.members || {}).total ?? 0} accent="text-alp-gold"
            sub={(data.members || {}).newThisWeek > 0
              ? <span className="text-emerald-300">+{data.members.newThisWeek} this week</span>
              : <span>Signed up via the gate &amp; account page</span>} />
          <Tile testid="overview-reviews-new" icon={Star} label="New reviews (14d)" value={engagement.reviewsNew}
            sub={engagement.reviewsPending ? <span className="text-amber-300">{engagement.reviewsPending} awaiting approval</span> : null}
            onClick={() => goTo('customers', 'reviews')} />
          <Tile testid="overview-contacts" icon={MessageSquare} label="Unanswered messages" value={engagement.newContacts}
            onClick={() => goTo('customers', 'contacts')} />
          <Tile testid="overview-low-stock-health" icon={AlertTriangle} label="Low / out of stock" value={lowStock.count} accent="text-amber-300"
            onClick={() => goTo('inventory', 'products')} />
          <Tile testid="overview-total-orders" icon={PackageCheck} label="Total orders" value={data.totalOrders}
            onClick={() => goTo('sales', 'orders')} />
        </div>
      </motion.div>

      {/* Recent orders */}
      <motion.div variants={fadeUp}>
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Recent Orders</p>
        <div className="border border-alp-line rounded-xl overflow-hidden">
          {recentOrders.length === 0 ? (
            <p className="text-alp-gray text-sm p-6 text-center">No orders yet.</p>
          ) : recentOrders.map((o) => (
            <button key={o.orderNumber} onClick={() => goTo('sales', 'orders')} data-testid={`overview-recent-${o.orderNumber}`}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-alp-line/50 last:border-0 bg-alp-card/30 hover:bg-alp-card/60 transition-colors text-left">
              <div className="min-w-0">
                <span className="text-alp-gold text-sm tracking-wide">{o.orderNumber}</span>
                <span className="text-alp-cream/80 text-sm ml-3 truncate">{o.name || '—'}</span>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className={`text-[11px] tracking-[0.12em] uppercase ${STATUS_STYLE[o.orderStatus] || 'text-alp-cream/80'}`}>{o.orderStatus}</span>
                <span className="text-alp-cream text-sm">{money(o.total)}</span>
              </div>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Lowstock detail */}
      {lowStock.items.length > 0 && (
        <motion.div variants={fadeUp}>
          <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Restock Watch</p>
          <div className="flex flex-wrap gap-2">
            {lowStock.items.map((p) => (
              <span key={p.id} className="text-[11px] border border-amber-400/30 text-amber-200/90 rounded px-3 py-1.5 bg-amber-400/5">
                {p.name} <span className="text-alp-gray">({p.size})</span> · {p.stockStatus === 'In Stock' ? `${p.inventory} left` : p.stockStatus}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
